import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from "react";
import { AlertCircle } from "lucide-react";
import { useLatestRef } from "../../../hooks/useLatestRef";
const FOCUSABLE_CONTROL = "button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])";
// At least `from`, enough to show `rect`'s bottom, never past its top.
function deltaToShow(rect, view, from = 0) {
    return Math.min(Math.max(from, rect.bottom - view.bottom), rect.top - view.top);
}
// Scrolls only the detail panel's own scroll container: `scrollIntoView` would
// also scroll the `overflow-hidden` React Flow wrapper the panel floats in.
// A frame taller than the container shows its top unless that hides the target.
function scrollIntoDetailView(frame, target) {
    const container = frame.closest("[data-detail-scroll]");
    if (!container) return;
    const view = container.getBoundingClientRect();
    const frameDelta = deltaToShow(frame.getBoundingClientRect(), view);
    container.scrollTop += target ? deltaToShow(target.getBoundingClientRect(), view, frameDelta) : frameDelta;
}
function revealAndFocus(frame) {
    const target = frame.querySelector("[data-field-issue-focus]") ?? frame.querySelector(FOCUSABLE_CONTROL);
    scrollIntoDetailView(frame, target);
    target?.focus({
        preventScroll: true
    });
}
// A callback ref keyed on the request id: React calls it again for every new
// id, so a repeat request on an already-mounted frame focuses it again. The
// reveal waits one frame because a freshly mounted detail panel caps its
// height in a follow-up render; measured earlier, the field would scroll away.
function useFocusRequestRef(focusRequestId, onFocusHandled) {
    const onFocusHandledRef = useLatestRef(onFocusHandled);
    return useCallback((frame)=>{
        if (focusRequestId === undefined || !frame) return;
        const pending = requestAnimationFrame(()=>{
            revealAndFocus(frame);
            onFocusHandledRef.current?.(focusRequestId);
        });
        return ()=>cancelAnimationFrame(pending);
    }, [
        focusRequestId,
        onFocusHandledRef
    ]);
}
/**
 * Wraps one detail-panel field. Flagged, it draws a red frame and lists the
 * messages under the field. Mark the control to focus with
 * `data-field-issue-focus` when it is not the first one in the field.
 */ export function FieldIssueFrame({ messages, focusRequestId, onFocusHandled, messagesId, children }) {
    const frameRef = useFocusRequestRef(focusRequestId, onFocusHandled);
    const isFlagged = (messages?.length ?? 0) > 0;
    return /*#__PURE__*/ _jsxs("div", {
        ref: frameRef,
        "data-invalid": isFlagged || undefined,
        className: isFlagged ? "-mx-2 rounded-md bg-destructive/5 p-2 ring-1 ring-destructive/50" : undefined,
        children: [
            children,
            isFlagged && /*#__PURE__*/ _jsx("ul", {
                id: messagesId,
                className: "mt-1.5 space-y-1.5",
                children: messages?.map((message, index)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-start gap-1.5 text-xs text-destructive",
                        children: [
                            /*#__PURE__*/ _jsx(AlertCircle, {
                                className: "mt-px size-3.5 shrink-0"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "font-medium",
                                        children: message.title
                                    }),
                                    message.hint && /*#__PURE__*/ _jsx("div", {
                                        className: "text-muted-foreground",
                                        children: message.hint
                                    })
                                ]
                            })
                        ]
                    }, index))
            })
        ]
    });
}

//# sourceMappingURL=FieldIssueFrame.js.map